import { CommonUtility } from '@utility/common';
import { APIPath } from '@utility/constants/api';
import { BaseService } from '@utility/services/base';
import type {
  ConfirmDecision,
  ConfirmResponse,
  MappingCounts,
  MappingSuggestionRow,
  MappingSuggestionsPage,
  MappingSuggestionsParams,
  StatusAction,
} from './suggestion-types';

const BASE = APIPath.catalogMappingSuggestions;

/** The server takes 1..200 decisions per confirm call and 1..500 ids per skip or restore call. */
export const CONFIRM_BATCH_SIZE = 200;
export const STATUS_BATCH_SIZE = 500;

/** The API names the listing's own SKU and title listingSku / listingTitle; the rows here call them sku / title. */
type ApiSuggestionRow = Omit<MappingSuggestionRow, 'sku' | 'title'> & {
  listingSku?: string | null;
  listingTitle?: string | null;
  sku?: string | null;
  title?: string | null;
};

const toRow = ({
  listingSku,
  listingTitle,
  sku,
  title,
  ...row
}: ApiSuggestionRow): MappingSuggestionRow => ({
  ...row,
  sku: listingSku ?? sku ?? null,
  title: listingTitle ?? title ?? null,
});

export const getMappingSuggestions = async (
  params: MappingSuggestionsParams,
): Promise<MappingSuggestionsPage> => {
  const page = await BaseService.get<
    Omit<MappingSuggestionsPage, 'data'> & { data: ApiSuggestionRow[] }
  >(`${BASE}?${CommonUtility.objectToParams(params)}`);
  // A body without rows (an error shape, a stub) passes through as it is: the hook treats a missing data as no rows.
  return Array.isArray(page?.data)
    ? { ...page, data: page.data.map(toRow) }
    : (page as MappingSuggestionsPage);
};

export const getMappingCounts = () =>
  BaseService.get<MappingCounts>(`${BASE}/counts`);

export const postConfirm = (decisions: ConfirmDecision[]) =>
  BaseService.post<ConfirmResponse>(`${BASE}/confirm`, { decisions });

export const postStatus = (action: StatusAction, listingIds: string[]) =>
  BaseService.post<unknown>(`${BASE}/${action}`, { listingIds });

/** Reruns the mapping pass for the account (auto-link off): every unlinked listing gets a stored suggestion. */
export const postRefresh = () =>
  BaseService.post<{ written: number; removed: number; refreshedAt: string }>(
    `${BASE}/refresh`,
  );

export const postUnmap = (listingId: string) =>
  BaseService.post<unknown>(
    `${APIPath.catalogListings}/${encodeURIComponent(listingId)}/unmap`,
  );
